// src/constants/scalableUnits.ts

export interface RoundingRule {
  min: number;
  max: number; // use Infinity for “no upper bound”
  increment: number;
}

export interface ScalableUnitConfig {
  promoteTo: string | null; // e.g. "kg" for "g"
  promotionThreshold: number; // e.g. 1000 for grams→kg
  roundingRules: RoundingRule[]; // list of { min, max, increment }
}

const SCALABLE_UNITS: Record<string, ScalableUnitConfig> = {
  // ─── “g” → “kg” (no change) ─────────────────────────────────────────────────
  g: {
    promoteTo: 'kg',
    promotionThreshold: 1000,
    roundingRules: [
      { min: 0, max: 100, increment: 1 },
      { min: 100, max: 500, increment: 5 },
      { min: 500, max: 1000, increment: 10 },
      { min: 1000, max: Infinity, increment: 0.1 },
    ],
  },
  kg: {
    promoteTo: null,
    promotionThreshold: Infinity,
    roundingRules: [
      { min: 0, max: 1, increment: 0.01 },
      { min: 1, max: 5, increment: 0.1 },
      { min: 5, max: Infinity, increment: 0.5 },
    ],
  },

  // ─── “ml” → “l” with friendlier steps ───────────────────────────────────────
  ml: {
    promoteTo: 'l',
    promotionThreshold: 1000,
    roundingRules: [
      { min: 0, max: 50, increment: 5 }, // 0–50 mL → 5 mL
      { min: 50, max: 500, increment: 25 }, // 50–500 mL → 25 mL
      { min: 500, max: 1000, increment: 50 }, // 500–1000 mL → 50 mL
      { min: 1000, max: Infinity, increment: 0.1 }, // ≥1000 mL → switch to “l”
    ],
  },
  l: {
    promoteTo: null,
    promotionThreshold: Infinity,
    roundingRules: [
      { min: 0, max: 1, increment: 0.01 },
      { min: 1, max: 5, increment: 0.1 },
      { min: 5, max: Infinity, increment: 0.5 },
    ],
  },

  // ─── “cup” → “qt” (no change) ─────────────────────────────────────────────
  cup: {
    promoteTo: 'qt',
    promotionThreshold: 4,
    roundingRules: [
      { min: 0, max: 1, increment: 0.125 },
      { min: 1, max: 2, increment: 0.25 },
      { min: 2, max: 4, increment: 0.5 },
      { min: 4, max: Infinity, increment: 1 },
    ],
  },
  qt: {
    promoteTo: 'gal',
    promotionThreshold: 4,
    roundingRules: [
      { min: 0, max: 1, increment: 0.1 },
      { min: 1, max: 4, increment: 0.5 },
      { min: 4, max: Infinity, increment: 1 },
    ],
  },
  gal: {
    promoteTo: null,
    promotionThreshold: Infinity,
    roundingRules: [
      { min: 0, max: 1, increment: 0.1 },
      { min: 1, max: Infinity, increment: 0.5 },
    ],
  },

  // ─── “tbsp” → “cup” with quarter‐Tbsp increments ───────────────────────────
  tbsp: {
    promoteTo: 'cup',
    promotionThreshold: 16, // 16 Tbsp = 1 cup
    roundingRules: [
      { min: 0, max: 1, increment: 0.25 }, // <1 Tbsp → ¼ Tbsp
      { min: 1, max: 4, increment: 0.25 }, // 1–4 Tbsp → ¼ Tbsp
      { min: 4, max: 16, increment: 1 }, // 4–16 Tbsp → 1 Tbsp
      { min: 16, max: Infinity, increment: 1 },
    ],
  },
  tsp: {
    promoteTo: 'tbsp',
    promotionThreshold: 3, // 3 tsp = 1 Tbsp
    roundingRules: [
      { min: 0, max: 1, increment: 0.25 }, // <1 tsp → ¼ tsp
      { min: 1, max: 3, increment: 0.5 }, // 1–3 tsp → ½ tsp
      { min: 3, max: Infinity, increment: 1 },
    ],
  },
};

export default SCALABLE_UNITS;
